import { useState, useEffect, useCallback } from 'react';
import { formatDateTime } from '@/utils/formatDate';
import {
    FaTachometerAlt, FaServer, FaPlay, FaPlus, FaTrash, FaEdit,
    FaSave, FaTimes, FaSync, FaCheckCircle, FaExclamationTriangle,
    FaArrowDown, FaArrowUp, FaClock, FaToggleOn, FaToggleOff,
    FaNetworkWired, FaChartLine
} from 'react-icons/fa';
import './BandwidthTestPanel.scss';
import * as Sentry from '@sentry/react';

function formatSpeed(bps) {
    if (!bps || bps === 0) return '0 bps';
    if (bps >= 1_000_000_000) return `${(bps / 1_000_000_000).toFixed(2)} Gbps`;
    if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(2)} Mbps`;
    if (bps >= 1_000) return `${(bps / 1_000).toFixed(2)} Kbps`;
    return `${bps} bps`;
}

function getPerformanceColor(percentage) {
    if (percentage == null) return '#6c757d';
    if (percentage >= 90) return '#28a745';
    if (percentage >= 70) return '#ffc107';
    if (percentage >= 50) return '#fd7e14';
    return '#dc3545';
}

function getPerformanceLabel(percentage) {
    if (percentage == null) return 'N/A';
    if (percentage >= 90) return 'Excellent';
    if (percentage >= 70) return 'Good';
    if (percentage >= 50) return 'Fair';
    return 'Poor';
}

/**
 * BandwidthTestPanel Component
 * Manages BTest server configuration, expected throughput per public IP,
 * bandwidth test schedules, manual test execution, and results history.
 */
const BandwidthTestPanel = ({ routerId, apiFetch }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [routerConfig, setRouterConfig] = useState(null);
    const [publicAddresses, setPublicAddresses] = useState([]);
    const [btestServers, setBtestServers] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [results, setResults] = useState([]);
    const [summary, setSummary] = useState([]);

    // UI state
    const [runningTest, setRunningTest] = useState(false);
    const [showScheduleForm, setShowScheduleForm] = useState(false);
    const [editingThroughput, setEditingThroughput] = useState(null);
    const [throughputForm, setThroughputForm] = useState({ downloadMbps: '', uploadMbps: '' });
    const [scheduleForm, setScheduleForm] = useState({
        sourceRouterId: parseInt(routerId),
        targetRouterId: '',
        publicAddressId: '',
        cronExpression: '0 2 * * *',
        protocol: 'tcp',
        testDurationSeconds: 10,
        displayName: ''
    });
    const [manualTestForm, setManualTestForm] = useState({
        targetRouterId: '',
        publicAddressId: '',
        protocol: 'tcp',
        testDurationSeconds: 10
    });

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const [configRes, addrRes, serversRes, schedulesRes, resultsRes, summaryRes] = await Promise.all([
                apiFetch(`/api/RouterConfig/${routerId}`),
                apiFetch(`/api/RouterConfig/${routerId}/public-addresses`),
                apiFetch('/api/RouterConfig/btest-servers'),
                apiFetch(`/api/RouterConfig/${routerId}/bandwidth-test-schedules`),
                apiFetch(`/api/RouterConfig/${routerId}/bandwidth-test-results?hours=168&limit=50`),
                apiFetch(`/api/RouterConfig/${routerId}/bandwidth-test-summary`)
            ]);

            if (configRes.ok) {
                const d = await configRes.json();
                if (d.success) setRouterConfig(d.data);
            }
            if (addrRes.ok) {
                const d = await addrRes.json();
                if (d.success) setPublicAddresses(d.data || []);
            }
            if (serversRes.ok) {
                const d = await serversRes.json();
                if (d.success) setBtestServers(d.data || []);
            }
            if (schedulesRes.ok) {
                const d = await schedulesRes.json();
                if (d.success) setSchedules(d.data || []);
            }
            if (resultsRes.ok) {
                const d = await resultsRes.json();
                if (d.success) setResults(d.data || []);
            }
            if (summaryRes.ok) {
                const d = await summaryRes.json();
                if (d.success) setSummary(d.data || []);
            }
        } catch (err) {
            Sentry.captureException(err);
            console.error('Error loading bandwidth test data:', err);
            setError(err.message || 'Failed to load bandwidth test data');
        } finally {
            setLoading(false);
        }
    }, [routerId, apiFetch]);

    useEffect(() => {
        (async () => { await loadData(); })();
    }, [loadData]);

    // ===== BTest Server Toggle =====
    const handleToggleBTestServer = async () => {
        try {
            const response = await apiFetch(`/api/RouterConfig/${routerId}/btest-server`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ isBTestServer: !routerConfig?.isBTestServer })
            });
            if (response.ok) {
                setRouterConfig(prev => ({ ...prev, isBTestServer: !prev?.isBTestServer }));
                // Refresh servers list
                const srvRes = await apiFetch('/api/RouterConfig/btest-servers');
                if (srvRes.ok) {
                    const d = await srvRes.json();
                    if (d.success) setBtestServers(d.data || []);
                }
            }
        } catch (err) {
            Sentry.captureException(err);
            alert(`Failed to update BTest server status: ${err.message}`);
        }
    };

    // ===== Expected Throughput =====
    const handleStartEditThroughput = (addr) => {
        setEditingThroughput(addr.id);
        setThroughputForm({
            downloadMbps: addr.expectedMaxDownloadMbps ?? '',
            uploadMbps: addr.expectedMaxUploadMbps ?? ''
        });
    };

    const handleSaveThroughput = async (addressId) => {
        try {
            const response = await apiFetch(`/api/RouterConfig/public-addresses/${addressId}/expected-throughput`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    downloadMbps: throughputForm.downloadMbps ? parseFloat(throughputForm.downloadMbps) : null,
                    uploadMbps: throughputForm.uploadMbps ? parseFloat(throughputForm.uploadMbps) : null
                })
            });
            if (response.ok) {
                setEditingThroughput(null);
                await loadData();
            } else {
                const d = await response.json();
                alert(d.error || 'Failed to update throughput');
            }
        } catch (err) {
            Sentry.captureException(err);
            alert(`Failed to save expected throughput: ${err.message}`);
        }
    };

    // ===== Schedule Management =====
    const handleCreateSchedule = async (e) => {
        e.preventDefault();
        try {
            const response = await apiFetch('/api/RouterConfig/bandwidth-test-schedules', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...scheduleForm,
                    sourceRouterId: parseInt(routerId),
                    targetRouterId: parseInt(scheduleForm.targetRouterId),
                    publicAddressId: scheduleForm.publicAddressId ? parseInt(scheduleForm.publicAddressId) : null,
                    testDurationSeconds: parseInt(scheduleForm.testDurationSeconds)
                })
            });
            const data = await response.json();
            if (data.success) {
                setShowScheduleForm(false);
                setScheduleForm({
                    sourceRouterId: parseInt(routerId),
                    targetRouterId: '',
                    publicAddressId: '',
                    cronExpression: '0 2 * * *',
                    protocol: 'tcp',
                    testDurationSeconds: 10,
                    displayName: ''
                });
                await loadData();
            } else {
                alert(data.error || 'Failed to create schedule');
            }
        } catch (err) {
            Sentry.captureException(err);
            alert(`Failed to create schedule: ${err.message}`);
        }
    };

    const handleToggleSchedule = async (scheduleId, currentEnabled) => {
        try {
            const response = await apiFetch(`/api/RouterConfig/bandwidth-test-schedules/${scheduleId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ isEnabled: !currentEnabled })
            });
            if (response.ok) await loadData();
        } catch (err) {
            Sentry.captureException(err);
            alert(`Failed to toggle schedule: ${err.message}`);
        }
    };

    const handleDeleteSchedule = async (scheduleId) => {
        if (!confirm('Delete this bandwidth test schedule?')) return;
        try {
            const response = await apiFetch(`/api/RouterConfig/bandwidth-test-schedules/${scheduleId}`, {
                method: 'DELETE'
            });
            if (response.ok) await loadData();
        } catch (err) {
            Sentry.captureException(err);
            alert(`Failed to delete schedule: ${err.message}`);
        }
    };

    // ===== Manual Test =====
    const handleRunManualTest = async () => {
        if (!manualTestForm.targetRouterId) {
            alert('Please select a BTest server target');
            return;
        }
        setRunningTest(true);
        try {
            const response = await apiFetch(`/api/RouterConfig/${routerId}/bandwidth-test`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    targetRouterId: parseInt(manualTestForm.targetRouterId),
                    publicAddressId: manualTestForm.publicAddressId ? parseInt(manualTestForm.publicAddressId) : null,
                    protocol: manualTestForm.protocol,
                    testDurationSeconds: parseInt(manualTestForm.testDurationSeconds)
                })
            });
            const data = await response.json();
            if (data.success) {
                await loadData();
            } else {
                alert(data.error || 'Bandwidth test failed');
            }
        } catch (err) {
            Sentry.captureException(err);
            alert(`Failed to run bandwidth test: ${err.message}`);
        } finally {
            setRunningTest(false);
        }
    };

    if (loading) {
        return (
            <div className="btest-panel">
                <div className="btest-loading">
                    <FaSync className="spinning" /> Loading bandwidth test configuration...
                </div>
            </div>
        );
    }

    return (
        <div className="btest-panel">
            <div className="btest-header">
                <h3><FaTachometerAlt /> Bandwidth Testing (BTest)</h3>
                <button onClick={loadData} className="refresh-btn" title="Refresh">
                    <FaSync />
                </button>
            </div>

            {error && (
                <div className="btest-error">
                    <FaExclamationTriangle /> {error}
                </div>
            )}

            {/* ===== BTest Server Toggle ===== */}
            <div className="btest-section">
                <div className="section-header">
                    <h4><FaServer /> BTest Server Configuration</h4>
                </div>
                <div className="btest-server-toggle">
                    <div className="toggle-row">
                        <span className="toggle-label">
                            Mark this router as a <strong>BTest Server</strong>
                        </span>
                        <button
                            className={`toggle-btn ${routerConfig?.isBTestServer ? 'active' : ''}`}
                            onClick={handleToggleBTestServer}
                        >
                            {routerConfig?.isBTestServer ? <><FaToggleOn /> Enabled</> : <><FaToggleOff /> Disabled</>}
                        </button>
                    </div>
                    <p className="toggle-hint">
                        When enabled, other routers can run bandwidth tests against this router.
                        Ensure the BTest server tool is enabled on the router&apos;s RouterOS configuration.
                    </p>
                </div>
            </div>

            {/* ===== Expected Throughput per Public IP ===== */}
            <div className="btest-section">
                <div className="section-header">
                    <h4><FaNetworkWired /> Expected Throughput per Public IP</h4>
                </div>
                {publicAddresses.length === 0 ? (
                    <div className="empty-state">
                        <p>No public IP addresses configured. Add public IPs on the Network tab to set expected throughput values.</p>
                    </div>
                ) : (
                    <div className="throughput-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Label</th>
                                    <th>IP Address</th>
                                    <th>Expected Download (Mbps)</th>
                                    <th>Expected Upload (Mbps)</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {publicAddresses.map(addr => (
                                    <tr key={addr.id}>
                                        <td className="label-cell">{addr.label}</td>
                                        <td className="ip-cell">{addr.ipAddress}</td>
                                        {editingThroughput === addr.id ? (
                                            <>
                                                <td>
                                                    <input
                                                        type="number"
                                                        value={throughputForm.downloadMbps}
                                                        onChange={(e) => setThroughputForm(prev => ({ ...prev, downloadMbps: e.target.value }))}
                                                        placeholder="e.g. 100"
                                                        className="throughput-input"
                                                        min="0"
                                                        step="0.1"
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        value={throughputForm.uploadMbps}
                                                        onChange={(e) => setThroughputForm(prev => ({ ...prev, uploadMbps: e.target.value }))}
                                                        placeholder="e.g. 50"
                                                        className="throughput-input"
                                                        min="0"
                                                        step="0.1"
                                                    />
                                                </td>
                                                <td>
                                                    <div className="action-btns">
                                                        <button onClick={() => handleSaveThroughput(addr.id)} className="save-btn" title="Save"><FaSave /></button>
                                                        <button onClick={() => setEditingThroughput(null)} className="cancel-btn" title="Cancel"><FaTimes /></button>
                                                    </div>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="speed-cell">
                                                    {addr.expectedMaxDownloadMbps != null ? `${addr.expectedMaxDownloadMbps} Mbps` : <span className="not-set">Not set</span>}
                                                </td>
                                                <td className="speed-cell">
                                                    {addr.expectedMaxUploadMbps != null ? `${addr.expectedMaxUploadMbps} Mbps` : <span className="not-set">Not set</span>}
                                                </td>
                                                <td>
                                                    <button onClick={() => handleStartEditThroughput(addr)} className="edit-btn" title="Edit expected throughput">
                                                        <FaEdit />
                                                    </button>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ===== Performance Summary Cards ===== */}
            {summary.length > 0 && (
                <div className="btest-section">
                    <div className="section-header">
                        <h4><FaChartLine /> Latest Test Results Summary</h4>
                    </div>
                    <div className="summary-cards">
                        {summary.map(s => {
                            const dlColor = getPerformanceColor(s.downloadPercentage);
                            const ulColor = getPerformanceColor(s.uploadPercentage);
                            return (
                                <div key={s.publicAddressId} className="summary-card">
                                    <div className="card-header">
                                        <span className="card-label">{s.label}</span>
                                        <span className="card-ip">{s.ipAddress}</span>
                                    </div>
                                    <div className="card-body">
                                        <div className="speed-metric">
                                            <FaArrowDown color="#17a2b8" />
                                            <div className="metric-detail">
                                                <span className="metric-value">
                                                    {s.lastDownloadMbps != null ? `${s.lastDownloadMbps} Mbps` : 'No data'}
                                                </span>
                                                {s.expectedMaxDownloadMbps && (
                                                    <span className="metric-expected">
                                                        / {s.expectedMaxDownloadMbps} Mbps expected
                                                    </span>
                                                )}
                                                {s.downloadPercentage != null && (
                                                    <span className="metric-performance" style={{ color: dlColor }}>
                                                        {s.downloadPercentage}% — {getPerformanceLabel(s.downloadPercentage)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="speed-metric">
                                            <FaArrowUp color="#fd7e14" />
                                            <div className="metric-detail">
                                                <span className="metric-value">
                                                    {s.lastUploadMbps != null ? `${s.lastUploadMbps} Mbps` : 'No data'}
                                                </span>
                                                {s.expectedMaxUploadMbps && (
                                                    <span className="metric-expected">
                                                        / {s.expectedMaxUploadMbps} Mbps expected
                                                    </span>
                                                )}
                                                {s.uploadPercentage != null && (
                                                    <span className="metric-performance" style={{ color: ulColor }}>
                                                        {s.uploadPercentage}% — {getPerformanceLabel(s.uploadPercentage)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {s.lastTestedAt && (
                                        <div className="card-footer">
                                            <FaClock /> Last tested: {formatDateTime(s.lastTestedAt)}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ===== Manual Test ===== */}
            <div className="btest-section">
                <div className="section-header">
                    <h4><FaPlay /> Run Manual Bandwidth Test</h4>
                </div>
                <div className="manual-test-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label>BTest Server (Target)</label>
                            <select
                                value={manualTestForm.targetRouterId}
                                onChange={(e) => setManualTestForm(prev => ({ ...prev, targetRouterId: e.target.value }))}
                            >
                                <option value="">Select a BTest server...</option>
                                {btestServers
                                    .filter(s => s.id !== parseInt(routerId))
                                    .map(s => (
                                        <option key={s.id} value={s.id}>{s.displayName} ({s.ipAddress})</option>
                                    ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Test via Public IP (optional)</label>
                            <select
                                value={manualTestForm.publicAddressId}
                                onChange={(e) => setManualTestForm(prev => ({ ...prev, publicAddressId: e.target.value }))}
                            >
                                <option value="">Management IP (default)</option>
                                {publicAddresses.map(a => (
                                    <option key={a.id} value={a.id}>{a.label} ({a.ipAddress})</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Protocol</label>
                            <select
                                value={manualTestForm.protocol}
                                onChange={(e) => setManualTestForm(prev => ({ ...prev, protocol: e.target.value }))}
                            >
                                <option value="tcp">TCP</option>
                                <option value="udp">UDP</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Duration (seconds)</label>
                            <input
                                type="number"
                                value={manualTestForm.testDurationSeconds}
                                onChange={(e) => setManualTestForm(prev => ({ ...prev, testDurationSeconds: e.target.value }))}
                                min="5"
                                max="60"
                            />
                        </div>
                        <div className="form-group form-actions">
                            <button
                                onClick={handleRunManualTest}
                                disabled={runningTest || !manualTestForm.targetRouterId}
                                className="run-test-btn"
                            >
                                {runningTest ? (
                                    <><FaSync className="spinning" /> Running Test...</>
                                ) : (
                                    <><FaPlay /> Run Test</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
                {btestServers.filter(s => s.id !== parseInt(routerId)).length === 0 && (
                    <div className="empty-state warning">
                        <FaExclamationTriangle /> No BTest servers available. Mark at least one other router as a BTest server to run tests.
                    </div>
                )}
            </div>

            {/* ===== Schedules ===== */}
            <div className="btest-section">
                <div className="section-header">
                    <h4><FaClock /> Scheduled Bandwidth Tests</h4>
                    <button onClick={() => setShowScheduleForm(!showScheduleForm)} className="add-btn">
                        {showScheduleForm ? <><FaTimes /> Cancel</> : <><FaPlus /> Add Schedule</>}
                    </button>
                </div>

                {showScheduleForm && (
                    <form onSubmit={handleCreateSchedule} className="schedule-form">
                        <div className="form-row">
                            <div className="form-group">
                                <label>Name</label>
                                <input
                                    type="text"
                                    value={scheduleForm.displayName}
                                    onChange={(e) => setScheduleForm(prev => ({ ...prev, displayName: e.target.value }))}
                                    placeholder="e.g. Daily Fibre Check"
                                />
                            </div>
                            <div className="form-group">
                                <label>BTest Server (Target)*</label>
                                <select
                                    value={scheduleForm.targetRouterId}
                                    onChange={(e) => setScheduleForm(prev => ({ ...prev, targetRouterId: e.target.value }))}
                                    required
                                >
                                    <option value="">Select a BTest server...</option>
                                    {btestServers
                                        .filter(s => s.id !== parseInt(routerId))
                                        .map(s => (
                                            <option key={s.id} value={s.id}>{s.displayName} ({s.ipAddress})</option>
                                        ))}
                                </select>
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Test via Public IP</label>
                                <select
                                    value={scheduleForm.publicAddressId}
                                    onChange={(e) => setScheduleForm(prev => ({ ...prev, publicAddressId: e.target.value }))}
                                >
                                    <option value="">Management IP (default)</option>
                                    {publicAddresses.map(a => (
                                        <option key={a.id} value={a.id}>{a.label} ({a.ipAddress})</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Cron Expression*</label>
                                <input
                                    type="text"
                                    value={scheduleForm.cronExpression}
                                    onChange={(e) => setScheduleForm(prev => ({ ...prev, cronExpression: e.target.value }))}
                                    placeholder="0 2 * * *"
                                    required
                                />
                                <span className="form-hint">e.g. &quot;0 2 * * *&quot; = daily at 2 AM</span>
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Protocol</label>
                                <select
                                    value={scheduleForm.protocol}
                                    onChange={(e) => setScheduleForm(prev => ({ ...prev, protocol: e.target.value }))}
                                >
                                    <option value="tcp">TCP</option>
                                    <option value="udp">UDP</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Duration (seconds)</label>
                                <input
                                    type="number"
                                    value={scheduleForm.testDurationSeconds}
                                    onChange={(e) => setScheduleForm(prev => ({ ...prev, testDurationSeconds: e.target.value }))}
                                    min="5"
                                    max="60"
                                />
                            </div>
                            <div className="form-group form-actions">
                                <button type="submit" className="save-btn">
                                    <FaSave /> Create Schedule
                                </button>
                            </div>
                        </div>
                    </form>
                )}

                {schedules.length > 0 ? (
                    <div className="schedules-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Target</th>
                                    <th>Via</th>
                                    <th>Schedule</th>
                                    <th>Protocol</th>
                                    <th>Duration</th>
                                    <th>Last Run</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {schedules.map(s => (
                                    <tr key={s.id} className={!s.isEnabled ? 'disabled-row' : ''}>
                                        <td>{s.displayName || `Schedule #${s.id}`}</td>
                                        <td>{s.targetRouterName || `Router #${s.targetRouterId}`}</td>
                                        <td>{s.publicAddressLabel || 'Mgmt IP'}</td>
                                        <td className="cron-cell">{s.cronExpression}</td>
                                        <td>{s.protocol?.toUpperCase()}</td>
                                        <td>{s.testDurationSeconds}s</td>
                                        <td>{s.lastRunAt ? formatDateTime(s.lastRunAt) : 'Never'}</td>
                                        <td>
                                            <span className={`status-badge ${s.isEnabled ? 'enabled' : 'disabled'}`}>
                                                {s.isEnabled ? 'Active' : 'Disabled'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="action-btns">
                                                <button
                                                    onClick={() => handleToggleSchedule(s.id, s.isEnabled)}
                                                    className={s.isEnabled ? 'disable-btn' : 'enable-btn'}
                                                    title={s.isEnabled ? 'Disable' : 'Enable'}
                                                >
                                                    {s.isEnabled ? <FaToggleOn /> : <FaToggleOff />}
                                                </button>
                                                <button onClick={() => handleDeleteSchedule(s.id)} className="delete-btn" title="Delete">
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="empty-state">
                        <p>No scheduled bandwidth tests. Click &quot;Add Schedule&quot; to create one.</p>
                    </div>
                )}
            </div>

            {/* ===== Recent Results ===== */}
            <div className="btest-section">
                <div className="section-header">
                    <h4><FaTachometerAlt /> Recent Test Results</h4>
                </div>
                {results.length > 0 ? (
                    <div className="results-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Target</th>
                                    <th>Via</th>
                                    <th><FaArrowDown /> Download</th>
                                    <th><FaArrowUp /> Upload</th>
                                    <th>Protocol</th>
                                    <th>Duration</th>
                                    <th>Type</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map(r => (
                                    <tr key={r.id} className={r.status === 'Failed' ? 'failed-row' : ''}>
                                        <td>{formatDateTime(r.testedAt)}</td>
                                        <td>{r.targetRouterName || `Router #${r.targetRouterId}`}</td>
                                        <td>{r.publicAddressLabel || 'Mgmt IP'}</td>
                                        <td className="speed-cell">{r.status === 'Completed' ? formatSpeed(r.downloadBps) : '-'}</td>
                                        <td className="speed-cell">{r.status === 'Completed' ? formatSpeed(r.uploadBps) : '-'}</td>
                                        <td>{r.protocol?.toUpperCase()}</td>
                                        <td>{r.testDurationSeconds}s</td>
                                        <td>{r.isScheduled ? 'Scheduled' : 'Manual'}</td>
                                        <td>
                                            <span className={`status-badge ${r.status?.toLowerCase()}`}>
                                                {r.status === 'Completed' && <FaCheckCircle />}
                                                {r.status === 'Failed' && <FaExclamationTriangle />}
                                                {r.status === 'Running' && <FaSync className="spinning" />}
                                                {' '}{r.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="empty-state">
                        <p>No bandwidth test results yet. Run a manual test or create a schedule to get started.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BandwidthTestPanel;
